import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { apiService } from '../../services/apiService';
import { Part } from '../../services/types';

/**
 * A part number box: type an Epicor part number and press Enter, or pick one of the parts matching what is typed
 * (UM number, Epicor number or description), searched after a short pause
 */
export const PartPicker: React.FC<{
  initial: string;
  onSelect: (partNum: string) => void;
}> = ({ initial, onSelect }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(initial);
  const [matches, setMatches] = useState<Part[]>([]);
  const [open, setOpen] = useState(false);
  const listId = useId();

  useEffect(() => setText(initial), [initial]);

  useEffect(() => {
    const query = text.trim();
    if (!open || query.length < 3) {
      setMatches([]);
      return;
    }
    let active = true;
    const timer = setTimeout(() => {
      apiService.getParts(query, 1, 8).then(
        (result) => active && setMatches(result.parts),
        () => active && setMatches([])
      );
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [text, open]);

  const choose = (partNum: string) => {
    setOpen(false);
    setText(partNum);
    if (partNum.trim()) onSelect(partNum.trim());
  };

  return (
    <form
      className="relative w-full max-w-xl"
      onSubmit={(e) => {
        e.preventDefault();
        choose(text);
      }}
    >
      <label htmlFor={`${listId}-input`} className="sr-only">
        {t('partDemand.picker.label')}
      </label>
      <div className="flex gap-2">
        <input
          id={`${listId}-input`}
          type="text"
          value={text}
          autoComplete="off"
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-controls={listId}
          placeholder={t('partDemand.picker.placeholder')}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <Search className="w-4 h-4" aria-hidden="true" />
          {t('partDemand.picker.show')}
        </button>
      </div>
      {open && matches.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-auto"
        >
          {matches.map((part) => (
            <li key={part.erpPartNum} role="option" aria-selected="false">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(part.erpPartNum)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
              >
                <span className="font-medium text-gray-900">
                  {part.erpPartNum}
                </span>
                {part.part && (
                  <span className="ml-2 text-gray-500">{part.part}</span>
                )}
                <span className="block text-gray-600 truncate">
                  {part.descriptionEN || part.descriptionFR}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};
